import { createContext, useContext, useState } from 'react';
import StageBadge from '@/components/shared/StageBadge.jsx';
import { formatCurrency } from '@/lib/format.js';
import { formatHumanDate, formatDateTime } from '@/lib/date-display.js';
import FactLine from './FactLine.jsx';
import { setV3NotARenewal, setV3PriorLink, setV3RewriteLink } from '@/services/v3.service.js';
import { anchorFor } from './doors.js';
import OpenDeal from './OpenDeal.jsx';
import OpenItem from './OpenItem.jsx';
import ScenarioBuilder from './ScenarioBuilder.jsx';
import { TermCommunications } from './CommunicationsSection.jsx';
import { AR_WORDS, AP_WORDS, STANDING_WORDS, PERIOD_SOURCE_WORDS, CLOSED_WORDS, COVERAGE_SOURCE_WORDS, NOT_RECORDED, NOT_STATED, SMOOTH_WORD, NOT_IN_MOTION, IN_MOTION, FOLD_TAIL, FALLBACK_FROM, SUCCESSOR_WORDS, REWRITE_LINK_ERROR_WORDS, AIRCRAFT_SOLD_WORDS, word, refKey } from './words.js';
import { emptyFacts, periodFallback, FOLD_GROUPS, FACT_LABELS } from './renewal-lead.js';

// ONE TERM. The current term renders open; an older term is one folded line
// (period, carrier, premium, state) and one tap opens the same block. Inside:
// the header (carrier, period, policy number or its input, the lifecycle
// state word from the payload with StageBadge), the four labelled dates,
// settled facts first (premium, taxes, fees, commission, billing), then what
// is in motion (A/R, A/P, carrier confirmation), then the coverage block
// (TermCoverage: liability, medical, hull, deductibles with not in motion
// before in motion), then quotes, pilots with their
// standing words, scenarios behind a disclosure that opens ScenarioBuilder,
// read-only), then the term's conversation (TermCommunications, stage D3:
// the payload's fold for this term, behind a disclosure). Every absent value
// is the NOT_RECORDED word.
// Lifecycle comes from terms[].lifecycle.state; nothing here computes it.
// Two pieces are exported for the phone column (PhoneLayout.jsx):
// TermEssentials (carrier, state, period, policy number or its input,
// premium) and TermMoney (the settled and in-motion money rows plus the
// ledger), so the phone renders the same rows and not a second copy.

const RENDERED = new Set(['policy_number', 'premium', 'effective_date', 'expiration_date', 'bound_at', 'paid_at', 'carrier', 'commission', 'ar_payment_status', 'ap_remit_status', 'carrier_confirmed_at', 'billing_method']);

function period(t) {
  const p = t.period || {};
  const s = formatHumanDate(p.effective_date); const e = formatHumanDate(p.expiration_date);
  if (s && e) return `${s} to ${e}`; if (s) return `from ${s}`; if (e) return `to ${e}`; return null;
}
function moneyOr(n) { return n == null ? null : formatCurrency(n); }
function dateOr(v) { return v ? formatHumanDate(v) : null; }
function whenOr(v) { return v ? formatDateTime(v) : null; }

// THE FOLD (2026-10-05). On an account with a renewal in progress (the route provides FoldProvider) the settled, in-motion and
// coverage rows that would print only "not recorded" are hidden behind ONE line with a count, and expand in place. Nothing is
// removed and no value changes: the hidden rows are exactly emptyFacts(t) (renewal-lead.js), and a row asks its enclosing FoldBox
// whether its label is on that list. Outside a FoldBox (the phone's Premium row, PendingTerm) a Row is always shown.
const FoldOn = createContext(false);
const FoldScope = createContext(null);
export const FoldProvider = FoldOn.Provider;

export function Row({ label, children, mono }) {
  const scope = useContext(FoldScope);
  const line = <FactLine label={label} value={children} mono={mono} />;
  if (scope && scope.labels.has(label)) return scope.open ? <div data-fact-empty={label}>{line}</div> : null;
  return line;
}

function GroupHeading({ group, children }) {
  const scope = useContext(FoldScope);
  if (scope && !scope.open && scope.hiddenGroups.has(group)) return null;
  return <div className="pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim" data-fact-group={group}>{children}</div>;
}

export function FoldBox({ t, groups = FOLD_GROUPS, children }) {
  const on = useContext(FoldOn);
  const outer = useContext(FoldScope);
  const [open, setOpen] = useState(false);
  if (!on || outer) return children;
  const empties = emptyFacts(t).filter((f) => groups.includes(f.group));
  const labels = new Set(empties.map((f) => f.label));
  const hiddenGroups = new Set(groups.filter((g) => FACT_LABELS[g].every((l) => labels.has(l))));
  return (
    <FoldScope.Provider value={{ labels, hiddenGroups, open }}>
      {children}
      {empties.length ? (
        <button type="button" className="inline-flex min-h-[36px] items-center py-1 text-sm text-text-muted underline-offset-2 hover:underline" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-fold-empty={t.id} data-fold-count={empties.length}>
          {empties.length} {empties.length === 1 ? 'field' : 'fields'} {FOLD_TAIL} {open ? '▾' : '▸'}
        </button>
      ) : null}
    </FoldScope.Provider>
  );
}

export function TermSummaryLine({ t, onOpen, notes = 0 }) {
  const carrier = t.carrier?.name || 'carrier not stated';
  return (
    <button type="button" onClick={onOpen} className="flex min-h-[44px] w-full flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-border py-2.5 text-left last:border-b-0 hover:bg-border/20" data-term-folded={t.id}>
      <span className="font-mono text-xs text-text-muted">{period(t) || 'period not recorded'}</span>
      <span className="text-sm font-medium text-text">{carrier}</span>
      <span className="font-mono text-sm text-text">{moneyOr(t.ledger?.bind?.premium) || NOT_RECORDED}</span>
      <StageBadge stage={t.lifecycle?.state} />
      {t.closed ? <span className="text-xs text-text-dim">{word(CLOSED_WORDS, t.closed.outcome)}{t.closed.aircraft_sold ? AIRCRAFT_SOLD_WORDS : ''}</span> : null}
      {t.succeeded_by ? <span className="text-xs text-text-dim" data-succeeded-by={t.succeeded_by.kind}>{word(SUCCESSOR_WORDS, t.succeeded_by.kind)}</span> : null}
      {notes ? <span className="text-xs text-text-muted" data-term-notes={notes}>{notes} open {notes === 1 ? 'note' : 'notes'}</span> : null}
      <span className="ml-auto text-xs text-text-dim">open ▸</span>
    </button>
  );
}

// The money of one term: settled first (premium, taxes, fees, commission,
// billing), then what is in motion (A/R, A/P, carrier confirmation) and the
// ledger's rows on demand. Rendered inside the term block and, on phone,
// under the Money row.
export function TermMoney({ t }) {
  const [showTxns, setShowTxns] = useState(false);
  const bind = t.ledger?.bind || null;
  const fees = bind?.fees ? Object.entries(bind.fees).map(([k, v]) => `${k.replace(/_/g, ' ')} ${formatCurrency(v)}`).join(', ') : null;
  return (
    <FoldBox t={t} groups={['Settled', 'In motion']}>
    <div data-term-money={t.id}>
        <GroupHeading group="Settled">Settled</GroupHeading>
        <Row label="Premium" mono>{moneyOr(bind?.premium)}</Row>
        <Row label="Taxes" mono>{moneyOr(bind?.estimated_taxes)}</Row>
        <Row label="Fees" mono>{fees}</Row>
        <Row label="Commission" mono>{bind?.commission != null ? `${formatCurrency(bind.commission)}${bind.commission_rate != null ? ` at ${bind.commission_rate}%` : ''}` : null}</Row>
        <Row label="Billing">{bind?.billing_method ? String(bind.billing_method).replace(/_/g, ' ') : null}</Row>

        <GroupHeading group="In motion">In motion</GroupHeading>
        <Row label="A/R">{bind ? <>{word(AR_WORDS, bind.ar_payment_status)}{bind.ar_paid_on ? <span className="ml-2 font-mono text-xs text-text-muted">{formatHumanDate(bind.ar_paid_on)}</span> : null}{bind.ar_method ? <span className="ml-2 text-xs text-text-dim">{bind.ar_method}</span> : null}{bind.ar_financed_by ? <span className="ml-2 text-xs text-text-dim">financed by {bind.ar_financed_by}</span> : null}</> : null}</Row>
        <Row label="A/P">{bind ? <>{word(AP_WORDS, bind.ap_remit_status)}{bind.ap_remit_at ? <span className="ml-2 font-mono text-xs text-text-muted">{formatDateTime(bind.ap_remit_at)}</span> : null}</> : null}</Row>
        <Row label="Carrier confirmed">{bind?.carrier_confirmed_at ? <>{formatDateTime(bind.carrier_confirmed_at)}{bind.carrier_confirmation_ref ? <span className="ml-2 text-xs text-text-dim">{bind.carrier_confirmation_ref}</span> : null}</> : null}</Row>
        <Row label="Ledger">{t.ledger ? (
          <span className="text-sm">
            <button type="button" className="min-h-[36px] text-text underline-offset-2 hover:underline" onClick={() => setShowTxns((v) => !v)} aria-expanded={showTxns}>{(t.ledger.transactions || []).length} transactions{t.ledger.voided_count ? `, ${t.ledger.voided_count} voided` : ''}</button>
            {showTxns ? (
              <ul className="mt-1 space-y-0.5 font-mono text-xs text-text-muted">
                {(t.ledger.transactions || []).map((x) => <li key={x.id}>{x.type} · {x.status} · {formatHumanDate(x.effective_date) || NOT_RECORDED} · {formatCurrency(x.premium_delta)} · A/R {word(AR_WORDS, x.ar_payment_status)} · A/P {word(AP_WORDS, x.ap_remit_status)}</li>)}
              </ul>
            ) : null}
          </span>
        ) : null}</Row>
    </div>
    </FoldBox>
  );
}

// The coverage of one term (contract rule 10): limits, medical, hull,
// deductibles, in the sketch's order, with the source word beside the label.
// Every null part renders NOT_STATED from words.js; nothing here derives a
// limit, a basis or a smooth flag: `smooth` earns its word only when the
// payload says true, false renders the sublimit, null claims neither. The
// deductibles row puts NOT IN MOTION before IN MOTION on purpose (the
// contract says JSON key order guarantees nothing, so the page orders it).
// A deductible is text as stored: numeric text formats as money, anything
// else ("5%", "$2,500") prints as stored. Rendered inside the full term block
// after the money rows and inside TermEssentials on the phone, so the phone
// shows the same block and not a second copy.
const NUMERIC_TEXT = /^\d+(\.\d+)?$/;
function deductible(v) {
  if (v == null || v === '') return null;
  return NUMERIC_TEXT.test(String(v).trim()) ? formatCurrency(Number(v)) : String(v);
}
function Part({ children }) {
  return children == null ? <span className="text-text-dim" data-state-word>{NOT_STATED}</span> : <span className="font-mono text-text">{children}</span>;
}
export function TermCoverage({ t }) {
  const c = t.coverage || {};
  const liab = c.liability || {}; const hull = c.hull || {}; const ded = c.deductibles || {};
  const sourceWord = c.source ? word(COVERAGE_SOURCE_WORDS, c.source) : null;
  return (
    <FoldBox t={t} groups={['Coverage']}>
    <div data-term-coverage={t.id} data-coverage-source={c.source || 'none'}>
      <GroupHeading group="Coverage">Coverage{sourceWord ? <span className="ml-2 normal-case tracking-normal text-text-dim" data-coverage-source-word>{sourceWord}</span> : null}</GroupHeading>
      <Row label="Liability">
        <span className="flex flex-wrap items-baseline gap-x-2 text-sm" data-coverage-liability>
          <Part>{moneyOr(liab.per_occurrence)}</Part>
          {liab.smooth === true ? <span className="text-xs text-text-muted">{SMOOTH_WORD}</span> : <><span className="text-text-dim">/</span><Part>{moneyOr(liab.per_passenger)}</Part><span className="text-xs text-text-muted">per passenger</span></>}
        </span>
      </Row>
      <Row label="Medical"><span className="text-sm" data-coverage-medical><Part>{moneyOr(c.medical_payments)}</Part></span></Row>
      <Row label="Hull">
        <span className="flex flex-wrap items-baseline gap-x-2 text-sm" data-coverage-hull>
          <Part>{moneyOr(hull.value)}</Part>
          {hull.basis ? <span className="text-xs text-text-muted">{hull.basis}</span> : <span className="text-xs text-text-dim" data-state-word>basis {NOT_STATED}</span>}
        </span>
      </Row>
      <Row label="Deductibles">
        <span className="flex flex-wrap items-baseline gap-x-3 text-sm" data-coverage-deductibles>
          <span><Part>{deductible(ded.not_in_motion)}</Part> <span className="text-xs text-text-muted">{NOT_IN_MOTION}</span></span>
          <span><Part>{deductible(ded.in_motion)}</Part> <span className="text-xs text-text-muted">{IN_MOTION}</span></span>
        </span>
      </Row>
    </div>
    </FoldBox>
  );
}

// The current term's essentials for the phone column: carrier and state in
// the header, then period, policy number (or its input when AIRgus asks),
// premium, then the coverage block. The same rows the full block renders,
// nothing more.
// The Link control (2026-10-13). An inferred later term reads "looks renewed, not linked": nothing counts it as renewed until the broker
// links it, and the click writes the link through POST /api/v3/policies/:successor/prior (policies.linkPrior). No read links a term.
function LinkRenewal({ t, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async (call, fallback) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try { await call(); onSaved?.(); }
    catch (e) { setErr(e?.code || e?.message || fallback); }
    finally { setBusy(false); }
  };
  const go = () => run(() => setV3PriorLink(t.succeeded_by.term_id, t.id), 'link failed');
  // The rewrite link (David 2026-11-01, N5450P): the later term is this one's rewrite (a new named insured), written through POST
  // /api/v3/policies/:later/rewrite-of (deal-rewrite.service linkRewrite, the one writer of the rewrite succession). A re-up has no button.
  const rewrite = () => run(async () => {
    try { await setV3RewriteLink(t.succeeded_by.term_id, t.id, 'rewrite'); }
    catch (e) { const w = Object.prototype.hasOwnProperty.call(REWRITE_LINK_ERROR_WORDS, e?.code) ? REWRITE_LINK_ERROR_WORDS[e.code] : null; if (w) { const x = new Error(w); x.code = w; throw x; } throw e; }
  }, 'rewrite link failed');
  // Not a renewal: recorded with who and when; the term keeps its line, and the start of a renewal from it proceeds for this later term only.
  const notRenewal = () => run(() => setV3NotARenewal(t.id, t.succeeded_by.term_id), 'could not record');
  return (
    <span id={anchorFor({ subject: 'term', id: t.id, field: 'succession' })} className="flex flex-wrap items-center gap-2" data-link-renewal>
      <button type="button" onClick={go} disabled={busy} className="min-h-[36px] rounded-card border border-border px-3 text-sm text-text hover:bg-border/20 disabled:opacity-60" data-link-renewal-button>{busy ? 'Linking...' : 'Link it as the renewal'}</button>
      <button type="button" onClick={rewrite} disabled={busy} className="min-h-[36px] rounded-card border border-border px-3 text-sm text-text hover:bg-border/20 disabled:opacity-60" data-link-rewrite-button>{busy ? 'Linking...' : 'Link it as the rewrite'}</button>
      <button type="button" onClick={notRenewal} disabled={busy} className="min-h-[36px] rounded-card px-3 text-sm text-text-muted hover:text-text disabled:opacity-60" data-not-renewal-button>Not a renewal of this term</button>
      {err ? <span className="text-xs text-danger" data-link-renewal-error>{err}</span> : null}
    </span>
  );
}

export function TermEssentials({ t, byRef, ctx, onSaved, remedyHref, onOpen }) {
  const bind = t.ledger?.bind || null;
  const items = byRef.get(refKey({ subject: 'term', id: t.id, field: 'policy_number' })) || [];
  const periodSrc = word(PERIOD_SOURCE_WORDS, t.period?.source);
  return (
    <section className="rounded-card border border-border-strong bg-surface" data-term-essentials={t.id}>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-2">
        <span className="text-[15px] font-semibold text-text">{t.carrier?.name || <span className="text-text-dim" data-state-word>carrier not stated</span>}</span>
        <StageBadge stage={t.lifecycle?.state} />
        {t.closed ? <span className="text-xs text-text-muted" data-closed>{word(CLOSED_WORDS, t.closed.outcome)}{t.closed.aircraft_sold ? AIRCRAFT_SOLD_WORDS : ''}</span> : null}
        {onOpen ? <button type="button" onClick={onOpen} className="ml-auto min-h-[36px] text-xs text-text-dim hover:text-text">all terms ▸</button> : null}
      </header>
      <div className="px-4">
        <Row label="Period" mono>{period(t) ? <>{period(t)}{periodSrc && t.period?.source !== 'ledger' ? <span className="ml-2 text-xs text-warning">{periodSrc}</span> : null}</> : null}</Row>
        <FactLine label="Policy #" mono fact_ref={{ subject: 'term', id: t.id, field: 'policy_number' }} items={items} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref} value={t.policy_number} />
        <Row label="Premium" mono>{moneyOr(bind?.premium)}</Row>
        <TermCoverage t={t} />
      </div>
    </section>
  );
}

export default function TermBlock({ t, people = [], byRef, ctx, onSaved, remedyHref, current = false, onFold, conversation = null }) {
  const itemsFor = (field) => byRef.get(refKey({ subject: 'term', id: t.id, field })) || [];
  const other = [...byRef.entries()].filter(([k]) => k.startsWith(`term:${t.id}:`) && !RENDERED.has(k.split(':')[2])).flatMap(([, v]) => v);
  const periodSrc = word(PERIOD_SOURCE_WORDS, t.period?.source);
  const personName = (id) => people.find((p) => p.id === id)?.display_name || 'person not on the account';
  const [showScenarios, setShowScenarios] = useState(false);
  return (
    <article className={`rounded-card border bg-surface ${current ? 'border-border-strong' : 'border-border'}`} data-term={t.id} data-current={current ? 'true' : 'false'}>
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
        <span className="text-[15px] font-semibold text-text">{t.carrier?.name || <span className="text-text-dim" data-state-word>carrier not stated</span>}</span>
        <span className="font-mono text-sm text-text-muted">{period(t) || <span data-state-word>period {NOT_RECORDED}</span>}</span>
        {periodSrc && t.period?.source !== 'ledger' ? <span className="text-xs text-warning">{periodSrc}</span> : null}
        <StageBadge stage={t.lifecycle?.state} />
        {t.lifecycle?.legacy ? <span className="text-[11px] text-text-dim">legacy</span> : null}
        {t.closed ? <span className="text-xs text-text-muted" data-closed>{word(CLOSED_WORDS, t.closed.outcome)}{t.closed.reason ? `: ${t.closed.reason}` : ''}</span> : null}
        {t.succeeded_by ? <span className="text-xs text-text-dim" data-succeeded-by={t.succeeded_by.kind}>{word(SUCCESSOR_WORDS, t.succeeded_by.kind)}</span> : null}
        {t.succeeded_by?.kind === 'inferred' ? <LinkRenewal t={t} onSaved={onSaved} /> : null}
        <span className="ml-auto flex items-center gap-3 text-xs text-text-dim">
          <span>{t.business_type || ''}</span>
          <OpenDeal account={ctx?.account} crmDealId={t.crm_deal_id} />
          {onFold ? <button type="button" onClick={onFold} className="min-h-[36px] hover:text-text">fold ▾</button> : null}
        </span>
      </header>
      <div className="px-4">
        <FactLine label="Policy #" mono fact_ref={{ subject: 'term', id: t.id, field: 'policy_number' }} items={itemsFor('policy_number')} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref} value={t.policy_number} />
        <Row label="Dates">
          <span className="flex flex-wrap gap-x-4 gap-y-1 text-sm" data-term-dates>
            {[['bound', whenOr(t.dates?.bound_at), null], ['effective', dateOr(t.dates?.effective_date), periodFallback(t, 'effective_date')], ['paid', whenOr(t.dates?.paid_at), null], ['expires', dateOr(t.dates?.expiration_date), periodFallback(t, 'expiration_date')]].map(([k, v, fb]) => (
              <span key={k}>
                <span className="text-text-muted">{k}</span>{' '}
                {v ? <span className="font-mono text-text">{v}</span> : fb ? <><span className="font-mono text-text" data-date-from-period={k}>{dateOr(fb.ymd)}</span> <span className="text-xs text-text-dim" data-period-fallback>{FALLBACK_FROM}{fb.source && word(PERIOD_SOURCE_WORDS, fb.source) ? `, ${word(PERIOD_SOURCE_WORDS, fb.source)}` : ''}</span></> : <span className="text-text-dim" data-state-word>{NOT_RECORDED}</span>}
              </span>
            ))}
          </span>
        </Row>
        {t.proposed_effective_date ? <Row label="Proposed" mono>{formatHumanDate(t.proposed_effective_date)} <span className="text-xs text-text-dim">proposal, not the period</span></Row> : null}

        <FoldBox t={t}>
          <TermMoney t={t} />
          <TermCoverage t={t} />
        </FoldBox>
        <Row label="Quotes">{t.quotes && t.quotes.count ? `${t.quotes.count} received, ${t.quotes.presented} presented${t.quotes.low_premium != null ? `, low ${formatCurrency(t.quotes.low_premium)} ${t.quotes.low_carrier || ''}` : ''}${t.quotes.bound_quote_id ? ', one bound' : ''}` : null}</Row>
        <Row label="Pilots">{t.pilots?.length ? (
          <span className="flex flex-col gap-0.5" data-term-pilots>
            {t.pilots.map((pp, i) => (
              <span key={pp.person_id + i} className={pp.ended_at ? 'text-text-dim line-through' : ''}>
                <a href={`#person-${pp.person_id}`} className="inline-flex min-h-[36px] items-center font-medium hover:underline">{personName(pp.person_id)}</a>
                {pp.registration ? <span className="ml-2 font-mono text-xs text-text-muted">{pp.registration}</span> : null}
                <span className="ml-2 text-xs text-text-muted" data-standing>{word(STANDING_WORDS, pp.standing)}</span>
                {pp.interest ? <span className="ml-2 text-xs text-text-dim">{pp.interest}</span> : null}
                {pp.pilot_requirements ? <span className="ml-2 text-xs text-warning">{pp.pilot_requirements}</span> : null}
                {pp.standing_reason ? <span className="ml-2 text-xs text-text-dim">{pp.standing_reason}</span> : null}
              </span>
            ))}
          </span>
        ) : null}</Row>
        <Row label="Scenarios">
          <button type="button" className="inline-flex min-h-[36px] items-center text-text underline-offset-2 hover:underline" onClick={() => setShowScenarios((v) => !v)} aria-expanded={showScenarios} data-scenarios-toggle={t.id}>{t.scenarios?.length ? `${t.scenarios.length} ${t.scenarios.length === 1 ? 'scenario' : 'scenarios'}` : 'none'} {showScenarios ? '▾' : '▸'}</button>
        </Row>
        {showScenarios ? <ScenarioBuilder t={t} people={people} aircraft={ctx?.aircraft || []} /> : null}
        <TermCommunications t={t} conversation={conversation} />
        {other.length ? (
          <div className="border-t border-border py-1" data-term-other-items>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Open on this term</div>
            {other.map((it, i) => <OpenItem key={it.code + i} item={it} compact href={remedyHref?.(it)} />)}
          </div>
        ) : null}
      </div>
    </article>
  );
}
